import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import toast from 'react-hot-toast';
import { CameraZonesTable } from './CameraZonesTable.tsx';
import {
  buildPostCamerasCreateBody,
  buildPutCamerasUpdateBody,
  type AddCameraFormValues,
} from './AddCameraDialog.tsx';
import React from 'react';
import { userTimezoneHeaders } from '../../utils/userTimezoneHeader';

declare const process: { env: Record<string, string | undefined> };
const API_BASE = process.env.REACT_APP_API_URL_FOR_AI_DETECTION || '';

type ApiErrorJson = {
  message?: string;
  detail?: string | Array<{ msg?: string }>;
  error?: { code?: string; current?: number; limit?: number };
};

function messageFromApiErrorJson(
  json: ApiErrorJson | null | undefined,
  fallback: string,
): string {
  if (json && typeof json.message === 'string' && json.message.trim()) {
    return json.message.trim();
  }
  if (json && typeof json.detail === 'string' && json.detail.trim()) {
    return json.detail.trim();
  }
  if (json && Array.isArray(json.detail)) {
    const parts = json.detail.map((d) => d?.msg).filter(Boolean) as string[];
    if (parts.length) return parts.join('; ');
  }
  return fallback;
}

/** GET /cameras `data[]` — legacy camera or zone-config row with use_cases. */
export interface CameraListItem {
  id?: string;
  camera_id?: string;
  zone_id?: string;
  zone_name?: string | null;
  name?: string;
  camera_ip?: string;
  streaming_url?: string;
  /** Legacy GET /cameras field; used only if `streaming_url` is absent. */
  stream_url?: string;
  is_active: boolean;
  zones_count?: number;
  preview_url?: string | null;
  created_at?: string;
  updated_at?: string;
  use_cases?: Array<{
    use_case_id?: string;
    use_case_name?: string | null;
    zone_profile_id?: string | null;
    severity?: string;
    deleted_at?: string | null;
    profile_count?: number;
  }>;
}

interface ZoneManagementTabProps {
  onZoneConfigChange?: (isActive: boolean) => void;
}

/*
 * Legacy: full `PageSettingsDialog` + ZONE_MANAGEMENT_SETTINGS state (display/sort/filter toggles).
 * Commented per request — Zone Management settings now uses the minimal modal only.
 *
 * import { PageSettingsDialog } from '../../common/PageSettingsDialog.tsx';
 * import { SettingsGroup } from '../../common/SettingsGroup.tsx';
 * import { SettingsSwitch, SettingsSelect } from '../../common/SettingsControls.tsx';
 */

export function ZoneManagementTab({ onZoneConfigChange }: ZoneManagementTabProps) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [cameras, setCameras] = useState<CameraListItem[]>([]);
  const [camerasLoading, setCamerasLoading] = useState(false);
  const [camerasError, setCamerasError] = useState<string | null>(null);

  // Fetch cameras when Zone Management tab is opened (GET /cameras)
  const fetchCameras = useCallback(async () => {
    if (!API_BASE.trim()) {
      setCamerasError('API base URL is not configured. Add REACT_APP_API_URL_FOR_AI_DETECTION to your .env file.');
      setCamerasLoading(false);
      return;
    }
    setCamerasLoading(true);
    setCamerasError(null);
    try {
      const url = `${API_BASE.replace(/\/$/, '')}/cameras`;
      const res = await fetch(url, {
        method: 'GET',
        headers: { Accept: 'application/json', ...userTimezoneHeaders() },
      });
      const json = (await res.json().catch(() => ({}))) as ApiErrorJson;
      if (!res.ok) {
        const errMsg = messageFromApiErrorJson(
          json,
          res.statusText || 'Failed to load cameras',
        );
        setCamerasError(errMsg);
        setCameras([]);
        return;
      }
      // Response format: { message: "Success", status: "success", data: [...] }
      const data = json?.data;
      const list = Array.isArray(data) ? data : [];
      setCameras(list);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load cameras';
      setCamerasError(message);
      setCameras([]);
    } finally {
      setCamerasLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCameras();
  }, [fetchCameras]);

  const handleCameraToggle = useCallback(
    async (cameraId: string, isActive: boolean) => {
      const base = API_BASE.replace(/\/$/, '');
      if (!base) return;
      setCameras((prev) =>
        prev.map((c) => {
          const cid = c.camera_id ?? c.id;
          return cid === cameraId ? { ...c, is_active: isActive } : c;
        }),
      );
      try {
        const res = await fetch(`${base}/cameras/${encodeURIComponent(cameraId)}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', ...userTimezoneHeaders() },
          body: JSON.stringify({ is_active: isActive }),
        });
        if (!res.ok) {
          const json = (await res.json().catch(() => ({}))) as ApiErrorJson;
          const errMsg = messageFromApiErrorJson(
            json,
            'Failed to update camera',
          );
          toast.error(errMsg);
          setCameras((prev) =>
            prev.map((c) => {
              const cid = c.camera_id ?? c.id;
              return cid === cameraId ? { ...c, is_active: !isActive } : c;
            }),
          );
          return;
        }
        await fetchCameras();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to update camera');
        setCameras((prev) =>
          prev.map((c) => {
            const cid = c.camera_id ?? c.id;
            return cid === cameraId ? { ...c, is_active: !isActive } : c;
          }),
        );
      }
    },
    [fetchCameras],
  );

  const handleAddCameraApply = useCallback(
    async (values: AddCameraFormValues) => {
      const base = API_BASE.replace(/\/$/, '');
      if (!base) {
        toast.error('API base URL is not configured.');
        throw new Error('No API base');
      }
      const body = buildPostCamerasCreateBody(values);
      try {
        const res = await fetch(`${base}/cameras`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            ...userTimezoneHeaders(),
          },
          body: JSON.stringify(body),
        });
        const json = (await res.json().catch(() => ({}))) as ApiErrorJson;
        if (!res.ok) {
          const msg = messageFromApiErrorJson(
            json,
            res.statusText || 'Failed to add camera',
          );
          toast.error(msg);
          throw new Error('ADD_CAMERA_HANDLED');
        }
        toast.success('Camera added successfully');
        await fetchCameras();
      } catch (e) {
        if (e instanceof Error && e.message === 'ADD_CAMERA_HANDLED') throw e;
        if (e instanceof Error && e.message === 'No API base') throw e;
        toast.error(e instanceof Error ? e.message : 'Failed to add camera');
        throw e;
      }
    },
    [fetchCameras],
  );

  const handleEditCameraApply = useCallback(
    async (cameraId: string, values: AddCameraFormValues) => {
      const base = API_BASE.replace(/\/$/, '');
      if (!base) {
        toast.error('API base URL is not configured.');
        throw new Error('No API base');
      }
      const body = buildPutCamerasUpdateBody(values);
      try {
        const res = await fetch(
          `${base}/cameras/${encodeURIComponent(cameraId)}`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              ...userTimezoneHeaders(),
            },
            body: JSON.stringify(body),
          },
        );
        const errText = await res.text();
        let errJson: ApiErrorJson = {};
        if (errText.trim()) {
          try {
            errJson = JSON.parse(errText) as ApiErrorJson;
          } catch {
            /* non-JSON error body */
          }
        }
        if (!res.ok) {
          const msg =
            messageFromApiErrorJson(
              errJson,
              res.statusText || 'Failed to update camera',
            ) || errText.trim();
          toast.error(msg);
          throw new Error('EDIT_CAMERA_HANDLED');
        }
        toast.success('Camera updated successfully');
        await fetchCameras();
      } catch (e) {
        if (e instanceof Error && e.message === 'EDIT_CAMERA_HANDLED') throw e;
        if (e instanceof Error && e.message === 'No API base') throw e;
        toast.error(e instanceof Error ? e.message : 'Failed to update camera');
        throw new Error('EDIT_CAMERA_HANDLED');
      }
    },
    [fetchCameras],
  );

  const handleDeleteCamera = useCallback(
    async (cameraId: string) => {
      const base = API_BASE.replace(/\/$/, '');
      if (!base) {
        toast.error('API base URL is not configured.');
        throw new Error('DELETE_CAMERA_HANDLED');
      }
      try {
        const res = await fetch(
          `${base}/cameras/${encodeURIComponent(cameraId)}`,
          {
            method: 'DELETE',
            /** Prefer JSON error bodies (422); 204 success has no body. */
            headers: { Accept: 'application/json', ...userTimezoneHeaders() },
          },
        );
        if (!res.ok) {
          const errText = await res.text();
          let errJson: ApiErrorJson = {};
          if (errText.trim()) {
            try {
              errJson = JSON.parse(errText) as ApiErrorJson;
            } catch {
              /* ignore */
            }
          }
          const msg =
            messageFromApiErrorJson(
              errJson,
              res.statusText || 'Failed to delete camera',
            ) || errText.trim();
          toast.error(msg);
          throw new Error('DELETE_CAMERA_HANDLED');
        }
        /** 204 No Content — successful delete */
        toast.success('Camera deleted successfully');
        await fetchCameras();
      } catch (e) {
        if (e instanceof Error && e.message === 'DELETE_CAMERA_HANDLED') throw e;
        toast.error(e instanceof Error ? e.message : 'Failed to delete camera');
        throw new Error('DELETE_CAMERA_HANDLED');
      }
    },
    [fetchCameras],
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
        flex: 1,
        minHeight: 0,
        width: '100%',
      }}
    >
      {/* Main content — same card shell as ManageUseCaseTab */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        style={{ minHeight: 0, display: 'flex', flexDirection: 'column', flex: 1 }}
      >
        <Paper
          elevation={1}
          sx={{
            height: 'calc(100vh - 168.8px)',
            maxHeight: 'calc(100vh - 168.8px)',
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '20px',
            overflow: 'hidden',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
          }}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <CameraZonesTable
              title="Manage Zones"
              onZoneConfigChange={onZoneConfigChange}
              cameras={cameras}
              camerasError={camerasError}
              camerasLoading={camerasLoading}
              onRetry={fetchCameras}
              onCameraToggle={handleCameraToggle}
              onSettingsClick={() => setSettingsOpen(true)}
              onAfterZoneConfigSave={fetchCameras}
              onAddCameraApply={handleAddCameraApply}
              onEditCameraApply={handleEditCameraApply}
              onDeleteCamera={handleDeleteCamera}
            />
          </Box>
        </Paper>
      </motion.div>

      {/* Zone Management settings: minimal modal (matches shared design reference) */}
      <Dialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        maxWidth="xs"
        fullWidth
        aria-labelledby="zone-management-settings-title"
      >
        <DialogTitle
          id="zone-management-settings-title"
          sx={{
            fontFamily: 'Roboto, sans-serif',
            fontSize: 18,
            fontWeight: 600,
            color: '#000000',
          }}
        >
          Settings
        </DialogTitle>
        <DialogContent>
          <Typography
            sx={{
              fontFamily: 'Roboto, sans-serif',
              fontSize: 14,
              color: '#212121',
            }}
          >
            Upcoming feature
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, justifyContent: 'flex-end' }}>
          <Button
            onClick={() => setSettingsOpen(false)}
            variant="contained"
            color="primary"
            sx={{
              textTransform: 'none',
              fontFamily: 'Roboto, sans-serif',
              fontWeight: 500,
              borderRadius: 1,
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/*
      Previous full settings UI (PageSettingsDialog + SettingsGroup / SettingsSwitch / SettingsSelect).
      const ZONE_MANAGEMENT_SETTINGS = { DISPLAY: {...}, SORTING: {...}, FILTERING: {...}, FEATURES: {...} };
      const [showCameraGroups, setShowCameraGroups] = useState(...);
      ... handleResetSettings ...
      <PageSettingsDialog open={settingsOpen} onClose=... title="Zone Management Settings" onReset={handleResetSettings}>
        <SettingsGroup title="Display Settings" ...>...</SettingsGroup>
        <SettingsGroup title="Sorting & Filtering" ...>...</SettingsGroup>
        <SettingsGroup title="Features" ...>...</SettingsGroup>
      </PageSettingsDialog>
      */}
    </Box>
  );
}
